import { useState, useEffect, useCallback } from 'react';
import { Star, BadgeCheck } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductReviews({ product }) {
  const [data, setData] = useState({ reviews: [], count: 0, average: 0 });
  const [nome, setNome] = useState('');
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [testo, setTesto] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/reviews/${product.id}`);
      if (res.ok) setData(await res.json());
    } catch (e) { /* noop */ }
  }, [product.id]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!nome.trim()) return toast.error('Inserisci il tuo nome');
    if (!rating) return toast.error('Seleziona una valutazione');
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: product.id, nome, rating, testo }),
      });
      if (!res.ok) throw new Error();
      toast.success('Grazie! La tua recensione è stata pubblicata.');
      setNome(''); setRating(0); setTesto('');
      load();
    } catch (e) {
      toast.error('Errore nell\'invio della recensione');
    } finally {
      setSubmitting(false);
    }
  };

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return ''; } };

  return (
    <section data-testid="product-reviews" className="mt-20">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-eyebrow mb-2">Recensioni</div>
          <h3 className="font-display text-2xl md:text-4xl">Cosa dicono i clienti</h3>
        </div>
        <div className="flex items-center gap-3" data-testid="reviews-summary">
          <span className="font-display text-4xl">{data.count ? data.average.toString().replace('.', ',') : '—'}</span>
          <div>
            <div className="flex gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} size={16} strokeWidth={1.5} className={i < Math.round(data.average) ? 'fill-essenya-ink text-essenya-ink' : 'text-essenya-border'} />
              ))}
            </div>
            <p className="text-essenya-text text-sm mt-1" data-testid="reviews-count">{data.count} {data.count === 1 ? 'recensione' : 'recensioni'}</p>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8">
        {/* Form */}
        <form onSubmit={submit} data-testid="review-form" className="bg-white rounded-[2rem] border border-essenya-border p-6 md:p-8 h-fit">
          <p className="font-display text-xl mb-5">Lascia la tua recensione</p>
          <label className="block text-sm text-essenya-text mb-2">La tua valutazione</label>
          <div className="flex gap-1 mb-5" data-testid="review-rating-input">
            {Array.from({ length: 5 }).map((_, i) => {
              const v = i + 1;
              return (
                <button key={v} type="button" onMouseEnter={() => setHover(v)} onMouseLeave={() => setHover(0)} onClick={() => setRating(v)} data-testid={`review-star-${v}`} aria-label={`${v} stelle`}>
                  <Star size={28} strokeWidth={1.5} className={(hover || rating) >= v ? 'fill-essenya-ink text-essenya-ink transition-colors' : 'text-essenya-border transition-colors'} />
                </button>
              );
            })}
          </div>
          <input
            data-testid="review-name-input"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Il tuo nome"
            className="w-full border border-essenya-border rounded-xl px-4 py-3 mb-3 bg-white/70 focus:outline-none focus:border-essenya-ink transition-colors"
          />
          <textarea
            data-testid="review-text-input"
            value={testo}
            onChange={(e) => setTesto(e.target.value)}
            placeholder="Raccontaci la tua esperienza con il prodotto..."
            rows={4}
            className="w-full border border-essenya-border rounded-xl px-4 py-3 mb-4 bg-white/70 focus:outline-none focus:border-essenya-ink transition-colors resize-none"
          />
          <button type="submit" disabled={submitting} data-testid="review-submit" className="btn-primary w-full disabled:opacity-50">
            {submitting ? 'Invio in corso...' : 'Pubblica recensione'}
          </button>
        </form>

        {/* List */}
        <div className="space-y-4" data-testid="reviews-list">
          {data.reviews.length === 0 ? (
            <div className="bg-essenya-surface2 rounded-[2rem] p-10 text-center text-essenya-text" data-testid="reviews-empty">
              Ancora nessuna recensione. Sii il primo a recensire questo prodotto!
            </div>
          ) : (
            data.reviews.map((r) => (
              <div key={r.id} data-testid={`review-item-${r.id}`} className="bg-white rounded-2xl border border-essenya-border p-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{r.nome}</p>
                    {r.verified && (
                      <span data-testid="review-verified-badge" className="inline-flex items-center gap-1 text-[11px] font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                        <BadgeCheck size={12} strokeWidth={2} /> Acquisto verificato
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-essenya-text">{fmtDate(r.created_at)}</span>
                </div>
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} size={14} strokeWidth={1.5} className={i < r.rating ? 'fill-essenya-ink text-essenya-ink' : 'text-essenya-border'} />
                  ))}
                </div>
                {r.testo && <p className="text-essenya-text leading-relaxed">{r.testo}</p>}
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
}
